<template>
  <v-container>
    <v-row id="techniques">
      <v-col cols="12">
        <h4 class="overline mb-1 d-flex">MITRE ATT&amp;CK&reg; Techniques</h4>
        <v-card flat color="bg_darkerish">
          <v-card-title id="techniqueTable" class="font-weight-light pt-0 pb-2">
            <v-row no-gutters>
              <v-col cols="12" order="1" sm="4" order-sm="1" class="px-2">
                <v-select
                  v-model="playFilters"
                  :items="playOptions"
                  :disabled="noMappedPlays"
                  @change="searchAndFilter"
                  multiple
                  label="Filter by Play"
                  color="dw_green"
                  item-color="dw_green"
                  item-text="id"
                  return-object
                  single-line
                  hide-details
                  clearable
                  :clear-icon="mdiClose"
                  class="text-caption"
                ></v-select>
              </v-col>
              <v-col cols="12" order="4" sm="4" order-sm="2" class="px-2">
                <v-select
                  v-model="tacticFilters"
                  :items="tacticOptions"
                  @change="searchAndFilter"
                  multiple
                  label="Filter by Tactic"
                  item-text="nickname"
                  color="dw_green"
                  item-color="dw_green"
                  return-object
                  single-line
                  hide-details
                  clearable
                  :clear-icon="mdiClose"
                  class="text-caption"
                >
                  <template v-slot:item="{ attrs, on, item }">
                    <v-list-item
                      ripple
                      v-bind="attrs"
                      v-on="on">
                      <v-list-item-action>
                        <v-icon
                          v-if="tacticFilters.includes(item)"
                          color="dw_green"
                          class="mr-2"
                          v-text="mdiCheckboxMarked" />
                        <v-icon
                          v-else
                          color="white"
                          class="mr-2"
                          v-text="mdiCheckboxBlankOutline" />
                      </v-list-item-action>
                      <v-list-item-content>
                        <v-list-item-title class="body-2 font-weight-light">
                          {{ item.display_name }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </template>
                </v-select>
              </v-col>
              <v-col cols="12" order="9" sm="4" order-sm="3" class="px-2">
                <v-text-field
                  v-model="search"
                  @keydown.enter="searchAndFilter"
                  label="Search"
                  color="dw_green"
                  clearable
                  @click:clear="() => search === null ? searchAndFilter : null"
                  :clear-icon="mdiClose"
                  single-line
                  hide-details
                  class="text-caption">
                  <template v-slot:append>
                    <v-icon small class="ml-1" v-text="mdiLayersSearchOutline" />
                  </template>
                </v-text-field>
              </v-col>
              <v-col cols="12" order="2" sm="4" order-sm="4" class="px-2 d-flex">
                <v-switch
                  v-model="noMappedPlays"
                  label="No Mapped Plays"
                  @change="searchAndFilter"
                  color="dw_green"
                  dense
                  hide-details
                ></v-switch>
                <v-switch
                  v-model="noSubs"
                  label="No Subtechniques"
                  @change="searchAndFilter"
                  color="dw_green"
                  dense
                  hide-details
                  class="ml-4"
                ></v-switch>
              </v-col>
              <v-col cols="12" order="5" sm="4" order-sm="5" class="px-2">
                <v-select
                  v-model="platformFilters"
                  :items="platformOptions"
                  @change="searchAndFilter"
                  multiple
                  label="Filter by Platform"
                  color="dw_green"
                  item-color="dw_green"
                  item-text="name"
                  return-object
                  single-line
                  hide-details
                  clearable
                  :clear-icon="mdiClose"
                  class="text-caption"
                ></v-select>
              </v-col>
              <v-col cols="12" order="7" sm="4" order-sm="6" class="px-2">
                <v-select
                  v-model="matrixFilter"
                  :items="matrixOptions"
                  @change="searchAndFilter"
                  label="Filter by MITRE Matrix"
                  color="dw_green"
                  item-color="dw_green"
                  item-text="name"
                  return-object
                  single-line
                  hide-details
                  clearable
                  :clear-icon="mdiClose"
                  class="text-caption"
                ></v-select>
              </v-col>
              <v-col cols="12" order="3" sm="4" order-sm="7" class="px-2">
                <v-radio-group row v-model="revokedOrDeprecated" @change="searchAndFilter">
                  <v-radio label="Current Techniques" value="false" color="dw_green" class="my-1" />
                  <v-radio
                    label="Include Deprecated"
                    value="unknown"
                    color="dw_green"
                    class="my-1" />
                  <v-radio label="Deprecated Only" value="true" color="dw_green" class="my-1" />
                </v-radio-group>
              </v-col>
              <v-col cols="12" order="6" sm="4" order-sm="8" class="px-2">
                <v-select
                  v-model="controlFilters"
                  :items="spControlOptions"
                  @change="searchAndFilter"
                  multiple
                  label="Filter by Control"
                  color="dw_green"
                  item-color="dw_green"
                  item-text="id"
                  item-value="id"
                  return-object
                  single-line
                  hide-details
                  clearable
                  :clear-icon="mdiClose"
                  class="text-caption"
                ></v-select>
              </v-col>
              <v-col cols="12" order="8" sm="4" order-sm="9" class="px-2">
                <v-select
                  v-model="groupFilters"
                  :items="groupOptions"
                  @change="searchAndFilter"
                  multiple
                  label="Filter by MITRE Group"
                  color="dw_green"
                  item-color="dw_green"
                  item-text="name"
                  return-object
                  single-line
                  hide-details
                  clearable
                  :clear-icon="mdiClose"
                  class="text-caption"
                ></v-select>
              </v-col>
            </v-row>
          </v-card-title>
          <v-data-table
            :headers="headers"
            :items="techniques"
            :items-per-page.sync="itemsPerPage"
            :page.sync="pageNum"
            :single-select="true"
            @click:row="(rowItem, { select }) => select(rowItem)"
            @item-selected="({ item }) =>
              $router.push({ name: 'technique_view', params: { id: item.id }})"
            item-key="mitre_id"
            show-select
            class="elevation-1 bg_darkerish"
            color="dw_green"
            hide-default-footer
          >
            <template v-slot:no-data>
              <div class="font-weight-light text-end">No Techniques found</div>
            </template>
            <template v-slot:item.data-table-select="{ isSelected, select }">
              <div @click="select">
                <v-icon v-if="isSelected" small color="dw_green" v-text="mdiRecordCircleOutline" />
                <v-icon v-else small color="white" v-text="mdiCircleOutline" />
              </div>
            </template>
            <template v-slot:item.mitre_id="{ value }">
              <div class="font-weight-light">{{ value }}</div>
            </template>
            <template v-slot:item.name="{ value }">
              <div class="text-capitalize font-weight-light">{{ value.toLowerCase() }}</div>
            </template>
            <template v-slot:item.matrix_type="{ value }">
              <div class="font-weight-light" v-html="displayMatrix(value)"></div>
            </template>

            <template v-slot:footer>
              <v-divider></v-divider>
              <v-row class="caption pa-2" no-gutters align="center" justify="center">
                <v-col cols="4" class="d-flex ml-2">
                  <v-row no-gutters align-content="start">
                    <v-col  cols="6" align-self="center">
                      <span class="grey--text">Rows per page:</span>
                    </v-col>
                    <v-col cols="4">
                      <v-select
                        dense
                        v-model="itemsPerPage"
                        :items="perPage"
                        item-text="text"
                        item-value="value"
                        color="dw_green"
                        item-color="dw_green"
                        single-line
                        hide-details
                        title="Rows per Page"
                        class="ml-2 pb-1 pl-1 my-auto caption"
                      ></v-select>
                    </v-col>
                  </v-row>
                </v-col>
                <v-spacer></v-spacer>
                <span class="mx-4 grey--text">
                  {{ pageStart + 1 }}-{{ pageStop }} of {{ techniqueCount }}
                </span>
                <v-btn
                  fab
                  x-small
                  text
                  class="mr-1"
                  title="Previous Page"
                  :disabled="prevPageDisabled"
                  @click="goToPrevPage">
                  <v-icon small v-text="mdiChevronLeft" />
                </v-btn>
                <v-btn
                  fab
                  x-small
                  text
                  class="mr-1"
                  title="Next Page"
                  :disabled="nextPageDisabled"
                  @click="goToNextPage">
                  <v-icon small v-text="mdiChevronRight" />
                </v-btn>
              </v-row>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>
    <router-view
      @filter-by-platform="filterByPlatform"
      :filters="{ pltfms: platformFilters }"
    ></router-view>
    <v-row class="caption px-2 py-1" no-gutters justify="start">
      <p class="caption font-weight-light grey--text mb-0">
        &copy; 2020 The MITRE Corporation. This work is reproduced and distributed with
        the permission of The MITRE Corporation.
      </p>
    </v-row>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import {
  mdiLayersSearchOutline,
  mdiCircleOutline,
  mdiRecordCircleOutline,
  mdiClose,
  mdiCheckboxBlankOutline,
  mdiCheckboxMarked,
  mdiChevronLeft,
  mdiChevronRight
} from '@mdi/js';

export default {
  name: 'techniquePanel',
  props: ['filters'],
  data() {
    return {
      mdiLayersSearchOutline,
      mdiCircleOutline,
      mdiRecordCircleOutline,
      mdiClose,
      mdiCheckboxBlankOutline,
      mdiCheckboxMarked,
      mdiChevronLeft,
      mdiChevronRight,
      search: '',
      playFilters: [],
      controlFilters: [],
      noSubs: true,
      noMappedPlays: false,
      tacticFilters: [],
      platformFilters: [],
      matrixFilter: '',
      matrixOptions: ['Enterprise', 'Mobile'],
      revokedOrDeprecated: 'false',
      groupFilters: [],
      headers: [
        {
          text: 'Technique',
          value: 'mitre_id',
          align: 'start',
          width: '10vw',
          sortable: false,
          class: 'font-weight-regular'
        },
        {
          text: 'Name',
          value: 'name',
          width: '30vw',
          sortable: false,
          class: 'font-weight-regular'
        },
        {
          text: 'Matrix Type',
          value: 'matrix_type',
          sortable: false,
          class: 'font-weight-regular'
        }
      ],
      itemsPerPage: 5,
      perPage: [
        { text: '5', value: 5 },
        { text: '25', value: 25 }
      ],
      pageNum: 1,
      numTablePages: 1,
      pageStart: 0,
      pageStop: 5,
      techniqueCount: 0,
      queryPrevPage: null,
      queryNextPage: null,
      queryPrevOffset: 0,
      queryCurrentOffset: 0,
      queryNextOffset: 25,
      prevPageDisabled: true,
      nextPageDisabled: false
    };
  },
  watch: {
    search() {
      if (this.search === null) {
        this.search = '';
        this.searchAndFilter();
      }
    },
    noMappedPlays() {
      this.playFilters = [];
    },
    techniquePagination() {
      this.handlePagination();
    },
    itemsPerPage() {
      this.pageNum = 1;
      this.handlePagination();
    },
    '$route.hash': {
      async handler(hash) {
        if (hash.length > 0) {
          this.search = hash.slice(1, hash.length);
          this.noSubs = false;
          this.searchAndFilter();
        }
      },
      immediate: true
    }
  },
  computed: {
    ...mapGetters([
      'techniques',
      'techniquePagination',
      'plays',
      'playOptions',
      'spControlOptions',
      'tacticOptions',
      'platformOptions',
      'groupOptions'
    ])
  },
  beforeRouteLeave(to, from, next) {
    this.noSubs = true;
    next();
  },
  mounted() {
    if (this.techniquePagination.count) this.handlePagination();
  },
  methods: {
    ...mapActions([
      'fetchTechniques'
    ]),
    filterByPlatform(type, pltfm) {
      if (type === 'add' && !this.platformFilters.some((fltr) => fltr.id === pltfm.id)) {
        this.platformFilters.push(pltfm);
      } else {
        this.platformFilters = this.platformFilters.filter((fltr) => fltr.id !== pltfm.id);
      }
      this.searchAndFilter();
      this.$vuetify.goTo('#techniqueTable');
    },
    displayMatrix(val) {
      if (val === 'enterprise') return this.matrixOptions[0];
      if (val === 'mobile') return this.matrixOptions[1];
      if (val === 'pre') return '<span>PRE <span class="ml-2">(Deprecated)</span></span>';
    },
    async searchAndFilter() {
      let endpoint = '?ordering=mitre_id';
      if (this.noSubs) endpoint += '&no_subs=true';
      endpoint += this.revokedOrDeprecated === 'true' ? '&rvkd_or_dprctd=true' : `&revoked=${this.revokedOrDeprecated}&deprecated=${this.revokedOrDeprecated}`;
      endpoint += this.search.length > 1 ? `&search=${this.search}` : '';
      if (this.noMappedPlays) endpoint += '&no_plays=true';
      this.playFilters.forEach((play) => { endpoint += `&plays__id=${play.id}`; });
      this.controlFilters.forEach((control) => { endpoint += `&controls__id=${control.id}`; });
      this.tacticFilters.forEach((tctc) => { endpoint += `&tactics__mitre_id=${tctc.mitre_id}`; });
      this.platformFilters.forEach((pltfm) => { endpoint += `&platforms__name=${pltfm.name}`; });
      this.groupFilters.forEach((grp) => { endpoint += `&group_procedures__group_id=${grp.id}`; });
      if (this.matrixFilter) endpoint += `&matrix_type=${this.matrixFilter.toLowerCase()}`;
      await this.fetchTechniques(endpoint);
    },
    handlePagination() {
      this.techniqueCount = this.techniquePagination.count;
      this.queryPrevPage = this.techniquePagination.previous;
      this.queryNextPage = this.techniquePagination.next;
      this.numTablePages = Math.ceil(this.techniques.length / this.itemsPerPage);
      if (this.queryPrevPage !== null) {
        this.queryPrevPage = new URL(this.queryPrevPage);
        const offset = this.queryPrevPage.searchParams.get('offset');
        this.queryPrevOffset = offset === null ? 0 : parseInt(offset, 10);
      }
      if (this.queryNextPage !== null) {
        this.queryNextPage = new URL(this.queryNextPage);
        const offset = this.queryNextPage.searchParams.get('offset');
        this.queryNextOffset = offset === null ? 0 : parseInt(offset, 10);
      }

      if (this.queryPrevPage === null) {
        this.queryCurrentOffset = 0;
      } else if (this.queryNextPage === null) {
        this.queryCurrentOffset = this.queryNextOffset;
      } else {
        this.queryCurrentOffset = (this.queryPrevOffset + this.queryNextOffset) / 2;
      }

      this.pageStart = this.queryCurrentOffset;
      let nextPageStop = this.queryCurrentOffset + this.itemsPerPage;
      nextPageStop = nextPageStop > this.techniqueCount ? this.techniqueCount : nextPageStop;
      this.pageStop = nextPageStop;
      this.prevPageDisabled = this.pageStart === 0;
      this.nextPageDisabled = this.pageStop === this.techniqueCount;
    },
    async goToNextPage() {
      if (this.pageNum === (this.techniques.length / this.itemsPerPage)) {
        await this.fetchTechniques(this.queryNextPage.search);
        let nextPageStop = this.queryCurrentOffset + this.itemsPerPage;
        nextPageStop = nextPageStop > this.techniqueCount ? this.techniqueCount : nextPageStop;
        this.pageNum = 1;
        this.pageStart = this.queryCurrentOffset;
        this.pageStop = nextPageStop;
      } else {
        this.pageNum += 1;
        this.pageStart += this.itemsPerPage;
        this.pageStop += this.itemsPerPage;
        if (this.pageStop > this.techniqueCount) this.pageStop = this.techniqueCount;
      }
      this.setDisabled();
    },
    async goToPrevPage() {
      if (this.pageNum === 1 && this.queryNextOffset > 0) {
        await this.fetchTechniques(this.queryPrevPage.search);
        this.pageNum = this.numTablePages;
        this.pageStart = this.queryNextOffset - this.itemsPerPage;
        this.pageStop = this.queryNextOffset;
      } else {
        this.pageNum -= 1;
        this.pageStart -= this.itemsPerPage;
        if (this.pageStop === this.techniqueCount) {
          this.pageStop = this.pageStart + this.itemsPerPage;
        } else this.pageStop -= this.itemsPerPage;
      }
      this.setDisabled();
    },
    setDisabled() {
      if (this.queryNextPage === null) {
        if (this.pageStop === this.techniqueCount) this.nextPageDisabled = true;
        else this.nextPageDisabled = false;
      } else this.nextPageDisabled = false;
      if (this.queryPrevPage === null) {
        if (this.pageStart === 0) this.prevPageDisabled = true;
        else this.prevPageDisabled = false;
      } else this.prevPageDisabled = false;
    }
  }
};
</script>
